import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { useTelegram } from '../../hooks/useTelegram';
import { useUserProfile } from '../../hooks/useUserProfile';
import { hapticFeedback } from '../../utils/telegram';
import Loader from '../common/Loader/Loader';
import './Referrals.css';
import shareLogo from '../../assets/icons/share-logo.svg';
import copyLogo from '../../assets/icons/copy-logo.svg';
import { formatNumber } from '../../utils/numUtils';
import { useConfig } from '../../hooks/useConfig';


const referalsLevel = {
  1500: "STARTERFOX",
  5000: "BASICFOX",
  25000: "TRADERFOX",
  100000: "PROFOX",
  500000: "EXPERTFOX",
  1500000: "ELITEFOX"

}

const Referrals = () => {
  const { user } = useUser();
  const { t } = useLanguage();
  const { getReferralInfo, getLevelReferrals } = useApi();
  const { showAlert } = useTelegram();
  const { displayName, photo } = useUserProfile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referalLevel, setReferalLevel] = useState(null);
  const config = useConfig();
  const [referralLink, setReferralLink] = useState(`${config.referralLink || 'https://t.me/wolfarbi_bot/base?startapp=rc_'}${user?.referralCode}`);

  useEffect(() => {
    console.log(config, user);
    setReferralLink(`${config.referralLink}${user?.referralCode}`);
  }, [config, user]);

  const [referralData, setReferralData] = useState({
    referralLink: referralLink,
    totalReferrals: 0,
    totalBalance: 0,
    totalDeposit: 0,
    totalProfit: 0,
    turnover: 0,
    levels: []
  });

  const [expandedLevels, setExpandedLevels] = useState({
    1: true,
    2: false,
    3: false,
    4: false,
    5: false,
  });
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);

  // New state for referrals modal
  const [showReferralsModal, setShowReferralsModal] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [levelReferrals, setLevelReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('registrationDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const itemsPerPage = 100;

  // Fetch referral data when component mounts
  useEffect(() => {
    const fetchReferralData = async () => {
      setLoading(true);
      try {
        const data = await getReferralInfo();

        if (data) {
          // Process and structure the API response to match our state format
          setReferralData({
            referralLink: data.referralLink || referralLink,
            totalReferrals: data?.summary?.totalReferrals || 0,
            totalBalance: data?.summary?.totalBalance || 0,
            totalDeposit: data?.summary?.totalDeposit || 0,
            totalProfit: data?.summary?.totalProfitFromUsers || 0,
            levels: data.levels || [],
            turnover: data.turnover || 0
          });

          let selectReferralLevel = 'STARTERFOX';
          for (const level in referalsLevel) {
            if (data.turnover >= level) {
              selectReferralLevel = referalsLevel[level];
              // break;
            }
          }
          setReferalLevel(selectReferralLevel);
        }
      } catch (err) {
        console.error('Error fetching referral data:', err);
        setError(t('failedToLoadReferralData'));
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();
  }, [getReferralInfo, user?.referralCode, t]);

  // Fetch level referrals when modal opens or parameters change
  useEffect(() => {
    if (showReferralsModal && currentLevel) {
      fetchLevelReferrals();
    }
  }, [showReferralsModal, currentLevel, currentPage, sortBy, sortOrder]);

  const fetchLevelReferrals = async () => {
    setReferralsLoading(true);
    try {
      const data = await getLevelReferrals({
        level: currentLevel,
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      });

      if (data) {
        setLevelReferrals(data.referrals || []);
        setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
      }
    } catch (err) {
      console.error('Error fetching level referrals:', err);
      // Clear referrals on error instead of showing mock data
      setLevelReferrals([]);
      setTotalPages(1);
    } finally {
      setReferralsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!referralLink) return;

    hapticFeedback.medium();
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        hapticFeedback.success();
        setShowCopiedNotification(true);
        setTimeout(() => {
          setShowCopiedNotification(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        hapticFeedback.error();
      });
  };

  const toggleLevelExpand = (level) => {
    hapticFeedback.light();
    setExpandedLevels(prev => ({
      ...prev,
      [level]: !prev[level]
    }));
  };

  const handleInfoClick = () => {
    hapticFeedback.medium();
    setShowInfoPopup(true);
  };

  const handleClosePopup = () => {
    hapticFeedback.light();
    setShowInfoPopup(false);
  };

  const handleViewReferrals = (level) => {
    hapticFeedback.medium();
    setCurrentLevel(level);
    setCurrentPage(1);
    setShowReferralsModal(true);
  };

  const handleCloseReferralsModal = () => {
    hapticFeedback.light();
    setShowReferralsModal(false);
    setCurrentLevel(null);
    setLevelReferrals([]);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy) => {
    hapticFeedback.light();
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    hapticFeedback.light();
    setCurrentPage(newPage);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ru-RU');
  };

  const getDefaultAvatar = () => {
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="20" fill="#4A90E2"/>
        <circle cx="20" cy="15" r="6" fill="white"/>
        <path d="M20 26c-7 0-12 3-12 7v2h24v-2c0-4-5-7-12-7z" fill="white"/>
      </svg>
    `)}`;
  };


  if (error) {
    return (
      <div className="referrals-page">
        <div className="referrals-error">
          <h2>{t('error')}</h2>
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={() => {
              setError(null);
              setLoading(true);
              // Перезапуск загрузки данных
              window.location.reload();
            }}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="referrals-page">
      {showCopiedNotification && (
        <div className="copied-notification">
          {t('copiedNotification')}
        </div>
      )}

      <div className="user-profile-card">
        <div className="user-profile-header">
          <div className="user-avatar-container">
            <img src={photo} alt="User avatar" className="user-avatar" />
          </div>
          <div className="user-name">
            {displayName}
          </div>
          <button className="info-button" onClick={() => {
            hapticFeedback.medium();
            handleInfoClick();
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 16V12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 8H12.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>


        <div className="referral-link-section">
          <div className="referral-link-label">{t('yourReferralLinkLabel')}</div>
          <div className="referral-link-container">
            <div className="referral-link-text">
              {referralLink.replace('https://t.me/', '')
                .replace('ssbase', '...')}
            </div>
            <div className="referral-link-actions">
              <button className="link-action-button copy" onClick={() => {
                hapticFeedback.medium();
                handleCopyLink();
              }}>
                <img src={copyLogo} alt="Copy" />
              </button>
              <button className="link-action-button share disabled-obj" onClick={() => hapticFeedback.light()}>
                <img src={shareLogo} alt="Share" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && <Loader fullPage={true} />}

      {!loading && (
        <>
          <h2 className="section-title">{t('referralStatsTitle')}</h2>

          <div className="referral-stats-card">
            <div className="referral-stat-row">
              <div className="stat-label">{t('numberOfActiveReferralsLabel')}</div>
              <div className="stat-value">{referralData?.totalReferrals || 0}</div>
            </div>
            <div className="referral-stat-row">
              <div className="stat-label">{t('totalReferralBalanceLabel')}</div>
              <div className="stat-value">{formatNumber(referralData?.totalBalance || 0)} USDT</div>
            </div>
            <div className="referral-stat-row">
              <div className="stat-label">{t('totalArbitrageProfitLabel')}</div>
              <div className="stat-value">{formatNumber(referralData?.totalProfit || 0)} USDT</div>
            </div>
            <div className="referral-stat-row">
              <div className="stat-label">{t('yourReferralsTurnover')}</div>
              <div className="stat-value">{formatNumber(referralData?.turnover || 0)} USDT</div>
            </div>
            <div className="referral-stat-row">
              <div className="stat-label">{t('yourReferralLevel')}</div>
              <div className="stat-value">{referalLevel}</div>
            </div>
          </div>

          <h2 className="section-title">{t('myReferralsTitle')}</h2>

          <div className="referral-levels">
            {referralData?.levels && referralData.levels.length > 0 ? (
              referralData.levels.map((level) => (
                <div className="referral-level-card" key={level.level}>
                  <div
                    className="level-header"
                    onClick={() => {
                      hapticFeedback.light();
                      toggleLevelExpand(level.level);
                    }}
                  >
                    <div className="level-title">{t('levelLabel', { level: level.level })}</div>
                    <div className="level-toggle">
                      {expandedLevels[level.level] ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 15L12 9L6 15" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9L12 15L18 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {expandedLevels[level.level] && (
                    <div className="level-details">
                      <div className="level-stats">
                        <div className="level-stat-column">
                          <div className="level-stat-label">{t('quantityLabel')}</div>
                          <div className="level-stat-value">{level.count || 0}</div>
                        </div>
                        <div className="level-stat-column">
                          <div className="level-stat-label">{t('balanceLabel')}</div>
                          <div className="level-stat-value">{formatNumber(level.balance || 0)} USDT</div>
                        </div>
                        <div className="level-stat-column">
                          <div className="level-stat-label">{t('profitLabel')}</div>
                          <div className="level-stat-value">{formatNumber(level.totalProfit || 0)} USDT</div>
                        </div>
                      </div>
                      {level.count > 0 && (
                        <div className="level-actions">
                          <button
                            className="view-referrals-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReferrals(level.level);
                            }}
                          >
                            {t('viewReferrals')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>{t('noReferralsYet')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {showInfoPopup && (
        <div className="referral-info-popup">
          <div className="referral-info-popup-content">
            <div className="referral-info-popup-header">
              <h2>{t('referralSystemConditionsTitle')}</h2>
              <button className="close-popup-button" onClick={() => {
                hapticFeedback.light();
                handleClosePopup();
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="referral-info-popup-description">
              <p>{t('referralSystemDescription')}</p>
            </div>

            <div className="referral-info-table-container">
              <table className="referral-info-table">
                <thead>
                  <tr>
                    <th>{t('statusTableHeader')}</th>
                    <th>{t('structureTurnoverTableHeader')}</th>
                    <th>{t('partnersCountTableHeader')}</th>
                    <th>{t('levelsDepthTableHeader')}</th>
                    <th>{t('commissionReductionTableHeader')}</th>
                    <th>{t('bonusDealsTableHeader')}</th>
                    <th>{t('maxDailyLimitTableHeader')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>STARTERFOX</td>
                    <td>1,500</td>
                    <td>1+</td>
                    <td>1 уровень</td>
                    <td>-3%</td>
                    <td>250 {config.tokenName || 'Token'} за 5,000</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>BASICFOX</td>
                    <td>5,000</td>
                    <td>3+</td>
                    <td>1 уровень</td>
                    <td>-5%</td>
                    <td>1 за 10,000</td>
                    <td>10</td>
                  </tr>
                  <tr>
                    <td>TRADERFOX</td>
                    <td>25,000</td>
                    <td>5+</td>
                    <td>2 уровня</td>
                    <td>-7%</td>
                    <td>2 за 15,000</td>
                    <td>25</td>
                  </tr>
                  <tr>
                    <td>PROFOX</td>
                    <td>100,000</td>
                    <td>10+</td>
                    <td>3 уровня</td>
                    <td>-10%</td>
                    <td>4 за 25,000</td>
                    <td>50</td>
                  </tr>
                  <tr>
                    <td>EXPERTFOX</td>
                    <td>500,000</td>
                    <td>15+</td>
                    <td>4 уровня</td>
                    <td>-12%</td>
                    <td>6 за 35,000</td>
                    <td>125</td>
                  </tr>
                  <tr>
                    <td>ELITEFOX</td>
                    <td>1,500,000</td>
                    <td>20+</td>
                    <td>5 уровней</td>
                    <td>-15%</td>
                    <td>8 за 50,000</td>
                    <td>300</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Referrals Modal */}
      {showReferralsModal && (
        <div className="referrals-modal-overlay">
          <div className="referrals-modal">
            <div className="referrals-modal-header">
              <h2>{t('levelReferralsTitle', { level: currentLevel })}</h2>
              <button className="close-modal-button" onClick={handleCloseReferralsModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="referrals-modal-controls">
              <div className="sort-controls">
                <label>{t('sortBy')}:</label>
                <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
                  <option value="registrationDate">{t('registrationDate')}</option>
                  <option value="deposit">{t('depositAmount')}</option>
                  <option value="profit">{t('profitAmount')}</option>
                </select>
                <button
                  className={`sort-order-button ${sortOrder}`}
                  onClick={() => handleSortChange(sortBy)}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>

            <div className="referrals-modal-content">
              {referralsLoading ? (
                <div className="referrals-loading">
                  <Loader size="small" />
                  <span>{t('loadingReferrals')}</span>
                </div>
              ) : (
                <>
                  {levelReferrals && levelReferrals.length > 0 ? (
                    <div className="referrals-list">
                      {levelReferrals.map((referral) => (
                        <div key={referral.id} className="referral-item">
                          <div className="referral-avatar">
                            <img
                              src={referral.avatar || getDefaultAvatar()}
                              alt={referral.firstName}
                              onError={(e) => {
                                e.target.src = getDefaultAvatar();
                              }}
                            />
                          </div>
                          <div className="referral-info">
                            <div className="referral-name">
                              {referral.firstName} {referral.lastName} ({referral.license})
                            </div>
                            <div className="referral-username">
                              @{referral.username}
                            </div>
                          </div>
                          <div className="referral-stats">
                            <div className="referral-stat">
                              <span className="stat-label">{t('balanceLabel')}:</span>
                              <span className="stat-value">{formatNumber(referral.balance || 0)} USDT</span>
                            </div>
                            <div className="referral-stat">
                              <span className="stat-label">{t('profitLabel')}:</span>
                              <span className="stat-value">{formatNumber(referral.totalProfit || 0)} USDT</span>
                            </div>
                            <div className="referral-stat">
                              <span className="stat-label">{t('registrationDate')}:</span>
                              <span className="stat-value">{formatDate(referral.registrationDate)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>{t('noReferralsAtThisLevel')}</p>
                    </div>
                  )}

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-button"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        {t('previousPage')}
                      </button>

                      <div className="pagination-info">
                        {t('pageInfo', { current: currentPage, total: totalPages })}
                      </div>

                      <button
                        className="pagination-button"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        {t('nextPage')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Referrals; 